import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useRegion } from "@/context/RegionContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Globe2, ShieldCheck, Star, Clock, Plus, Send, MapPin, Building2,
  Briefcase, Handshake, GraduationCap, ExternalLink, Inbox, Loader2,
} from "lucide-react";

const CATS = ["Business", "Collaboration", "Education"];
const CAT_ICON = { Business: Briefcase, Collaboration: Handshake, Education: GraduationCap };
const field = "w-full bg-black/50 border border-white/10 px-4 py-2.5 text-bone focus:outline-none focus:border-gold/50 text-sm";
const lbl = "text-xs uppercase tracking-widest text-bone/60 mb-1.5 block";

function VerifyBadge({ status }) {
  if (status === "verified")
    return <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 px-2.5 py-1" data-testid="badge-verified"><ShieldCheck size={13} /> Verified Credentials</span>;
  if (status === "unverified")
    return <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-400/40 px-2.5 py-1" data-testid="badge-unverified"><Star size={13} fill="currentColor" /> Has not completed verification of credentialing</span>;
  return <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-bone/50 border border-white/15 px-2.5 py-1" data-testid="badge-pending"><Clock size={13} /> Verification Pending</span>;
}

export default function Network() {
  const { user } = useAuth();
  const { region } = useRegion();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  const [intl, setIntl] = useState(false);
  const [regionOnly, setRegionOnly] = useState(false);
  const [showPost, setShowPost] = useState(false);
  const [inquireOpp, setInquireOpp] = useState(null);
  const [mine, setMine] = useState([]);
  const [showMine, setShowMine] = useState(false);

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (cat) q.set("category", cat);
    if (intl) q.set("international", "true");
    if (regionOnly && region.country) q.set("country", region.country);
    api.get(`/opportunities?${q.toString()}`).then((r) => setItems(r.data));
  }, [cat, intl, regionOnly, region.country]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user) api.get("/opportunities/mine").then((r) => setMine(r.data)).catch(() => {}); }, [user, showPost]);

  const onConnect = (opp) => {
    if (!user) { toast.info("Please register or sign in to connect."); nav("/join"); return; }
    setInquireOpp(opp);
  };
  const onPost = () => {
    if (!user) { toast.info("Please register your business to post an opportunity."); nav("/join"); return; }
    setShowPost(true);
  };

  return (
    <main className="bg-onyx min-h-screen pt-16" data-testid="network-page">
      <div className="relative border-b border-white/10 grain bg-surface overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/40 via-onyx to-onyx" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4 flex items-center gap-2"><Globe2 size={16} /> Global Diaspora Network</p>
          <h1 className="font-serif text-4xl md:text-6xl text-bone mb-4 max-w-3xl">Business, Collaboration & Education Across the Diaspora</h1>
          <p className="text-bone/60 max-w-2xl text-lg mb-8">Connect with businesses, institutions and changemakers throughout Africa and her Diaspora. Post opportunities, find international partners, and build across borders — credentials verified for trust.</p>
          <div className="flex flex-wrap gap-4">
            <button onClick={onPost} data-testid="post-opportunity-btn" className="bg-gold text-black px-8 py-3 font-medium hover:bg-gold2 transition-colors flex items-center gap-2"><Plus size={18} /> Post an Opportunity</button>
            {user && <button onClick={() => setShowMine((s) => !s)} data-testid="my-network-btn" className="border border-white/20 text-bone px-8 py-3 hover:bg-white/5 transition-colors flex items-center gap-2"><Inbox size={18} /> My Opportunities & Inquiries</button>}
            {!user && <button onClick={() => nav("/join")} data-testid="join-network-btn" className="border border-white/20 text-bone px-8 py-3 hover:bg-white/5 transition-colors">Register Your Business</button>}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        {showMine && user && <MyNetwork mine={mine} />}

        <div className="flex flex-wrap items-center gap-2 mb-10">
          <button onClick={() => setCat("")} className={`px-4 py-2 text-sm transition-colors ${!cat ? "bg-gold text-black" : "border border-white/15 text-bone/70 hover:border-gold/40"}`} data-testid="opp-filter-all">All</button>
          {CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)} className={`px-4 py-2 text-sm transition-colors ${cat === c ? "bg-gold text-black" : "border border-white/15 text-bone/70 hover:border-gold/40"}`} data-testid={`opp-filter-${c.toLowerCase()}`}>{c}</button>
          ))}
          {region.country && (
            <button onClick={() => setRegionOnly((v) => !v)} data-testid="opp-filter-region"
              className={`px-4 py-2 text-sm transition-colors flex items-center gap-1.5 ${regionOnly ? "bg-gold text-black" : "border border-white/15 text-bone/70 hover:border-gold/40"}`}>
              <span>{region.flag}</span> {region.label} only
            </button>
          )}
          <label className="ml-auto flex items-center gap-2 text-sm text-bone/70 cursor-pointer select-none" data-testid="opp-intl-toggle">
            <input type="checkbox" checked={intl} onChange={(e) => setIntl(e.target.checked)} className="accent-gold w-4 h-4" />
            <Globe2 size={16} className="text-gold" /> International only
          </label>
        </div>

        {items.length === 0 ? (
          <p className="text-bone/40 py-20 text-center" data-testid="no-opportunities">No opportunities match your filters yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="opportunities-grid">
            {items.map((o) => {
              const Icon = CAT_ICON[o.category] || Briefcase;
              return (
                <div key={o.id} className="bg-surface border border-white/10 hover:border-gold/40 transition-colors p-6 flex flex-col" data-testid="opportunity-card">
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gold"><Icon size={15} /> {o.category}</span>
                    {o.is_international && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-400/30 px-2 py-0.5"><Globe2 size={11} /> International</span>}
                  </div>
                  <h3 className="font-serif text-2xl text-bone mb-2 leading-tight">{o.title}</h3>
                  <p className="text-bone/40 text-xs flex items-center gap-1.5 mb-3"><MapPin size={12} /> {o.region ? `${o.region}, ` : ""}{o.country}</p>
                  <p className="text-bone/60 text-sm leading-relaxed mb-4 line-clamp-4">{o.description}</p>
                  <p className="text-bone/40 text-xs flex items-center gap-1.5 mb-4"><Building2 size={12} /> {o.business_name}</p>
                  <div className="mb-5"><VerifyBadge status={o.verification_status} /></div>
                  <button onClick={() => onConnect(o)} data-testid="connect-btn" className="mt-auto w-full bg-gold text-black py-2.5 font-medium hover:bg-gold2 transition-colors flex items-center justify-center gap-2"><Send size={15} /> Connect</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showPost && <PostForm onClose={() => setShowPost(false)} onDone={() => { setShowPost(false); load(); }} user={user} />}
      {inquireOpp && <InquireForm opp={inquireOpp} onClose={() => setInquireOpp(null)} />}
    </main>
  );
}

function MyNetwork({ mine }) {
  return (
    <div className="mb-12 border border-white/10 bg-surface p-6" data-testid="my-network-panel">
      <h2 className="font-serif text-2xl text-bone mb-4 flex items-center gap-2"><Inbox size={20} className="text-gold" /> My Opportunities & Inquiries</h2>
      {mine.length === 0 ? <p className="text-bone/50 text-sm">You haven't posted any opportunities yet.</p> : (
        <div className="space-y-4">
          {mine.map((o) => (
            <div key={o.id} className="border border-white/10 p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-bone font-medium">{o.title}</span>
                <VerifyBadge status={o.verification_status} />
              </div>
              {o.verification_note && <p className="text-amber-300/80 text-xs mt-2">Note: {o.verification_note}</p>}
              <div className="mt-3">
                <p className="text-xs uppercase tracking-widest text-gold mb-2">Inquiries ({o.inquiries?.length || 0})</p>
                {o.inquiries?.length ? o.inquiries.map((iq) => (
                  <div key={iq.id} className="text-sm text-bone/70 border-l-2 border-gold/40 pl-3 py-1 mb-2">
                    <span className="text-bone">{iq.from_name}</span> <a href={`mailto:${iq.from_email}`} className="text-gold">{iq.from_email}</a>
                    <p className="text-bone/60">{iq.message}</p>
                  </div>
                )) : <p className="text-bone/40 text-sm">No inquiries yet.</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PostForm({ onClose, onDone, user }) {
  const [f, setF] = useState({ title: "", category: "Business", description: "", country: "", region: "", is_international: true, contact_name: user?.name || "", contact_email: user?.email || "", website: "", business_name: "", business_registration: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/opportunities", f);
      toast.success("Opportunity submitted! Credentials will be reviewed by an administrator.");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to post"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-surface border-white/10 text-bone max-h-[90vh] overflow-y-auto" data-testid="post-opportunity-form">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Post an Opportunity</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><label className={lbl}>Title</label><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={field} data-testid="opp-title" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Category</label><select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={field} data-testid="opp-category">{CATS.map((c) => <option key={c}>{c}</option>)}</select></div>
            <label className="flex items-end gap-2 text-sm text-bone/70 pb-2.5 cursor-pointer"><input type="checkbox" checked={f.is_international} onChange={(e) => setF({ ...f, is_international: e.target.checked })} className="accent-gold w-4 h-4" /> International opportunity</label>
          </div>
          <div><label className={lbl}>Description</label><textarea required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={field} rows={4} data-testid="opp-description" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Country</label><input required value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className={field} data-testid="opp-country" /></div>
            <div><label className={lbl}>Region / City</label><input value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} className={field} /></div>
          </div>
          <div className="border-t border-white/10 pt-4">
            <p className="text-xs uppercase tracking-widest text-gold mb-3">Business Credentials (verified by admin)</p>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={lbl}>Business Name</label><input required value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} className={field} data-testid="opp-business-name" /></div>
              <div><label className={lbl}>Registration No.</label><input value={f.business_registration} onChange={(e) => setF({ ...f, business_registration: e.target.value })} className={field} data-testid="opp-business-reg" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div><label className={lbl}>Contact Name</label><input required value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} className={field} /></div>
              <div><label className={lbl}>Contact Email</label><input required type="email" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} className={field} /></div>
            </div>
            <div className="mt-4"><label className={lbl}>Website (optional)</label><input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className={field} placeholder="https://" /></div>
          </div>
          <button type="submit" disabled={busy} data-testid="opp-submit" className="w-full bg-gold text-black py-3 font-medium hover:bg-gold2 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">{busy && <Loader2 size={16} className="animate-spin" />} Submit for Review</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InquireForm({ opp, onClose }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post(`/opportunities/${opp.id}/inquire`, { message });
      setSent(data);
      toast.success("Inquiry sent!");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to send"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-surface border-white/10 text-bone" data-testid="inquire-form">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Connect · {opp.title}</DialogTitle></DialogHeader>
        {sent ? (
          <div className="space-y-4">
            <p className="text-emerald-300">{sent.message}</p>
            {sent.contact_email && <p className="text-bone/70 text-sm">Direct contact: <a href={`mailto:${sent.contact_email}`} className="text-gold">{sent.contact_email}</a></p>}
            <button onClick={onClose} className="bg-gold text-black px-6 py-2.5 font-medium hover:bg-gold2 transition-colors">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-bone/60 text-sm">Introduce yourself and your interest. The poster will receive your name and email to respond.</p>
            <textarea required value={message} onChange={(e) => setMessage(e.target.value)} className={field} rows={5} placeholder="Your message..." data-testid="inquire-message" />
            <button type="submit" disabled={busy} data-testid="inquire-submit" className="w-full bg-gold text-black py-3 font-medium hover:bg-gold2 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">{busy && <Loader2 size={16} className="animate-spin" />} <Send size={15} /> Send Inquiry</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
